
import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface ImageOptimizerProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  priority?: boolean;
  loadingStyles?: string;
  maxRetries?: number;
  retryDelay?: number;
}

const ImageOptimizer: React.FC<ImageOptimizerProps> = ({
  src,
  alt,
  className,
  fallbackSrc = '/placeholder.svg',
  priority = false,
  loadingStyles,
  maxRetries = 3,
  retryDelay = 1000,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(!priority);
  const [imgSrc, setImgSrc] = useState(priority ? src : '');
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const attemptImageLoad = (imageSrc: string, attempt: number = 0) => {
    console.log(`Attempting to load image: ${imageSrc} (attempt ${attempt + 1})`);
    
    const img = new Image();
    
    img.onload = () => {
      console.log(`Successfully loaded image: ${imageSrc} on attempt ${attempt + 1}`);
      setImgSrc(imageSrc);
      setIsLoading(false);
      setError(false);
      setRetryCount(0);
    };
    
    img.onerror = () => {
      console.error(`Failed to load image: ${imageSrc} on attempt ${attempt + 1}`);
      
      if (attempt < maxRetries) {
        const nextAttempt = attempt + 1;
        setRetryCount(nextAttempt);
        console.log(`Retrying image load in ${retryDelay}ms (attempt ${nextAttempt + 1}/${maxRetries + 1})`);
        
        retryTimeoutRef.current = setTimeout(() => {
          attemptImageLoad(imageSrc, nextAttempt);
        }, retryDelay);
      } else {
        console.error(`All retry attempts failed for image: ${imageSrc}. Using fallback.`);
        setError(true);
        setIsLoading(false);
        setImgSrc(fallbackSrc);
        setRetryCount(0);
      }
    };
    
    img.src = imageSrc;
    
    if (img.complete) {
      setImgSrc(imageSrc);
      setIsLoading(false);
      setError(false);
      setRetryCount(0);
    }
  };

  useEffect(() => {
    if (priority) return;

    setIsLoading(true);
    setError(false);
    setRetryCount(0);
    
    attemptImageLoad(src);
    
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [src, fallbackSrc, priority, maxRetries, retryDelay]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {isLoading && (
        <div className="absolute inset-0">
          <Skeleton className={cn("w-full h-full", loadingStyles)} />
          {retryCount > 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/50 text-white text-xs px-2 py-1 rounded">
                Tentativa {retryCount}/{maxRetries + 1}
              </div>
            </div>
          )}
        </div>
      )}
      
      {(!isLoading || priority) && (
        <img
          src={error ? fallbackSrc : imgSrc}
          alt={alt}
          className={cn(className, isLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300")}
          loading={priority ? "eager" : "lazy"}
          {...props}
        />
      )}
    </div>
  );
};

export default ImageOptimizer;
